import { createComputed } from "solid-js";
import { Record } from "record-tuple";

import Analytics from "@repo/utils/Analytics";
import Money from "@repo/utils/Money";
import UFunction from "@repo/utils/UFunction";

import appSettings from "~/appSettings";
import { useUser } from "~/state/features/user";
import Marketing from "~/utils/Marketing";

import { Registration } from "../../client";

declare namespace FacebookPixel {
  // https://developers.facebook.com/docs/meta-pixel/advanced/advanced-matching/
  type AdvancedMatching = {
    /**
     * Email
     *
     * Unhashed lowercase or hashed SHA-256
     *
     * @example jsmith@example.com
     * @example 6e3913852f512d76acff15d1e402c7502a5bbe6101745a7120a2a4833ebd2350
     */
    em?: string;
    /**
     * First Name
     *
     * Lowercase letters
     *
     * @example john
     */
    fn?: string;
    /**
     * Last Name
     *
     * Lowercase letters
     *
     * @example smith
     */
    ln?: string;
    /**
     * Phone
     *
     * Digits only including country code and area code
     *
     * @example 16505554444
     */
    ph?: string;
    /**
     * External ID
     *
     * Any unique ID from the advertiser, such as loyalty membership ID, user ID, and external cookie ID.
     *
     * @example 1234567890
     */
    external_id?: string;
    /**
     * Gender
     *
     * Single lowercase letter, f or m, if unknown, leave blank
     *
     * @example f
     */
    ge?: "f" | "m" | "";
    /**
     * Birthdate
     *
     * Digits only with birth year, month, then day
     *
     * @example 19910526 for May 26, 1991.
     */
    db?: string;
    /**
     * City
     *
     * Lowercase letters with any spaces removed
     *
     * @example sanfrancisco
     */
    ct?: string;
    /**
     * State or Province
     *
     * Lowercase two-letter state or province code
     *
     * @example ca
     */
    st?: string;
    /**
     * Zip or Postal Code
     *
     * Digits only
     *
     * @example 94025
     */
    zp?: string;
    /**
     * Country
     *
     * Lowercase two-letter country code
     *
     * @example us
     */
    country?: string;
  };

  type EventOptions = {
    content_category?: string;
    content_ids?: (string | number)[];
    content_name?: string;
    content_type?: string;
    contents?: { id: string; quantity: number }[];
    currency?: Money.Currency;
    num_items?: number;
    predicted_ltv?: number;
    search_string?: string;
    status?: boolean;
    value?: number;
  };

  // https://developers.facebook.com/docs/meta-pixel/reference/
  type TrackPropertiesByName = {
    Purchase: Pick<
      EventOptions,
      "content_ids" | "content_type" | "contents" | "num_items"
    > &
      Required<Pick<EventOptions, "currency" | "value">>;

    AddPaymentInfo: Pick<
      EventOptions,
      "content_category" | "content_ids" | "contents" | "currency" | "value"
    >;
    AddToCart: Pick<
      EventOptions,
      | "content_ids"
      | "content_name"
      | "content_type"
      | "contents"
      | "currency"
      | "value"
    >;
    AddToWish: Pick<
      EventOptions,
      | "content_name"
      | "content_category"
      | "content_ids"
      | "contents"
      | "currency"
      | "value"
    >;
    CompleteRegistration: Pick<
      EventOptions,
      "content_name" | "currency" | "status" | "value"
    >;
    InitiateCheckout: Pick<
      EventOptions,
      | "content_category"
      | "content_ids"
      | "contents"
      | "currency"
      | "num_items"
      | "value"
    >;
    Lead: Pick<
      EventOptions,
      "content_category" | "content_name" | "currency" | "value"
    >;
    Schedule: Pick<
      EventOptions,
      | "content_category"
      | "content_ids"
      | "content_type"
      | "contents"
      | "currency"
      | "search_string"
      | "value"
    >;
    StartTrial: Pick<EventOptions, "currency" | "predicted_ltv" | "value">;
    Subscribe: Pick<EventOptions, "currency" | "predicted_ltv" | "value">;
    ViewContent: Pick<
      EventOptions,
      | "content_ids"
      | "content_category"
      | "content_name"
      | "content_type"
      | "contents"
      | "currency"
      | "value"
    >;
  };

  export type Extra = {
    eventID?: string;
  };
}

declare global {
  interface Window {
    fbq(
      method: "init",
      id: string,
      advancedMatching?: FacebookPixel.AdvancedMatching
    ): void;
    fbq(method: "track", name: "PageView"): void;
    fbq<Name extends keyof FacebookPixel.TrackPropertiesByName>(
      method: "track",
      name: Name,
      ...[
        properties,
        extra,
      ]: {} extends FacebookPixel.TrackPropertiesByName[Name]
        ? [
            properties?: FacebookPixel.TrackPropertiesByName[Name],
            extra?: FacebookPixel.Extra,
          ]
        : [
            properties: FacebookPixel.TrackPropertiesByName[Name],
            extra?: FacebookPixel.Extra,
          ]
    ): void;
    fbq(
      method: "trackCustom",
      name: string,
      properties?: Analytics.Properties
    ): void;
  }
}

const id = appSettings.marketing.facebookId;

export default (): Registration.Conversion => {
  if (!id) return;
  if (import.meta.env.APP_ENV !== "production") return;

  // prettier-ignore
  // @ts-expect-error
  // eslint-disable-next-line
  !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');

  const init = UFunction.memo(
    (advancedMatching: FacebookPixel.AdvancedMatching) => {
      window.fbq("init", id, advancedMatching);
    },
    { by: ([advancedMatching]) => Record(advancedMatching) }
  );

  init({});
  window.fbq("track", "PageView");

  let eventID: string | undefined;

  return {
    identify({ userId }) {
      if (userId) eventID = userId;

      const [user] = useUser();

      createComputed(() => {
        const pii = user.pii();
        if (pii)
          init({
            em: pii.email,
            fn: pii.name.first,
            ln: pii.name.last,
            ph: pii.phone,
            // Must be lowercase with no spaces,
            ct: pii.address.locality.replace(/\s+/g, "").toLowerCase(),
            zp: pii.address.postalCode,
          });
      });
    },
    anonymize() {
      eventID = undefined;
      init({});
      init.invalidate.all();
    },
    track(name, properties) {
      const expectedLtv = Marketing.useExpectedLtv(properties);

      if (name === "Make Credit Line Payment")
        window.fbq(
          "track",
          "Purchase",
          {
            currency: appSettings.currency,
            value: expectedLtv,
          },
          { eventID }
        );
      else window.fbq("trackCustom", name, properties);
    },
  };
};
